import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@mui/material";

/**
 * Fades the page in whenever the path changes. It replays an animation on the
 * same element rather than keying on the path, so pages never remount and keep
 * their state; search-param-only changes don't animate. No fill mode is used,
 * so the transform is gone once it finishes and can't affect fixed children.
 */
const PageTransition: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const animation = el.animate(
      [
        { opacity: 0, transform: "translateY(8px)" },
        { opacity: 1, transform: "none" },
      ],
      { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" }
    );
    return () => animation.cancel();
  }, [pathname]);

  return (
    <Box
      ref={ref}
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        minWidth: 0,
        "& > *": { minWidth: 0 },
      }}
    >
      {children}
    </Box>
  );
};

export default PageTransition;
